import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import VideoCard from '@/components/feature/VideoCard';
import { hotVideos } from '@/mocks/homepage';
import { articleDetails, getRelatedVideos } from '@/mocks/articles';
import { categoryVideos } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleDetails[id] : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">视频不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedIds = getRelatedVideos(article.id, article.categorySlug);
  const allVideos = { ...categoryVideos[article.categorySlug]?.reduce((acc: Record<string, { id: string; title: string; imageUrl: string; duration: string; views: string; date: string; isHot?: boolean; isNew?: boolean }>, v) => { acc[v.id] = v; return acc; }, {}) };
  const relatedVideos = relatedIds
    .map((rid: string) => {
      const found = hotVideos.find((v) => v.id === rid);
      if (found) return found;
      for (const catSlug of Object.keys(categoryVideos)) {
        const cv = categoryVideos[catSlug]?.find((v) => v.id === rid);
        if (cv) return { ...cv, category: article.category, categorySlug: article.categorySlug };
      }
      return null;
    })
    .filter(Boolean) as Array<{
    id: string;
    title: string;
    category: string;
    categorySlug: string;
    imageUrl: string;
    duration: string;
    views: string;
    date: string;
  }>;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '美女视频精选合集',
    },
    publisher: {
      '@type': 'Organization',
      name: '美女视频精选合集',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.example.com/article/${article.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.example.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 美女视频精选合集`}
        description={article.description}
        keywords={`${article.category},美女视频,高清美女视频,${article.tags.join(',')}`}
        canonicalUrl={`https://www.example.com/article/${article.id}`}
        ogType="article"
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="w-full px-4 md:px-6 pb-14">
            <div className="max-w-4xl mx-auto">
              <div className="relative overflow-hidden rounded-xl mb-8 aspect-video">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={article.title}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/90 text-primary-600 cursor-pointer hover:bg-primary-500 hover:text-white transition-all duration-300">
                    <i className="ri-play-fill text-4xl"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1.5 rounded-lg font-label">
                  {article.duration}
                </div>
              </div>

              <header className="mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium text-xs whitespace-nowrap hover:bg-primary-200 transition-colors"
                  >
                    {article.category}
                  </Link>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views} 次观看
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                </div>
              </header>

              <div className="bg-background-100/70 rounded-xl p-6 md:p-8 mb-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3">视频简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{article.description}</p>
                <div className="mt-5 pt-5 border-t border-background-200/60">
                  <p className="text-sm text-foreground-700 leading-relaxed">{article.content}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-12">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedVideos.length > 0 && (
              <div className="max-w-6xl mx-auto border-t border-background-200/60 pt-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {relatedVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      id={video.id}
                      title={video.title}
                      category={video.category}
                      categorySlug={video.categorySlug}
                      imageUrl={video.imageUrl}
                      duration={video.duration}
                      views={video.views}
                      date={video.date}
                    />
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}